import ReactGA from 'react-ga4';
import type { IAnalyticsRepository } from '@src/domain/analytics/repository/analytics_repository';
import type { AnalyticsConfig } from '@src/domain/analytics/entities/analytics_config.entity';
import type { AnalyticsEvent } from '@src/domain/analytics/value_objects/analytics_event';
import type { PageView } from '@src/domain/analytics/value_objects/page_view';
import type { UserProperties } from '@src/domain/analytics/value_objects/user_properties';

/**
 * Google Analytics 4 Repository Implementation
 * Implements the IAnalyticsRepository interface using Google Analytics 4 (GA4)
 * 
 * This implementation translates domain analytics operations into GA4 tracking calls,
 * following Clean Architecture principles by keeping GA4-specific code in the infrastructure layer.
 */
export class GoogleAnalyticsRepositoryImpl implements IAnalyticsRepository {
    private initialized = false;
    private config: AnalyticsConfig | null = null;

    async initialize(config: AnalyticsConfig): Promise<void> {
        try {
            this.config = config;

            if (!config.isActive()) {
                console.warn('Analytics is disabled or in test mode');
                return;
            }

            // Initialize Google Analytics 4
            ReactGA.initialize(config.measurementId, {
                testMode: config.testMode,
                gaOptions: {
                    // Enable client ID storage to persist user identity across sessions
                    storage: 'cookies',
                    client_storage: 'cookies',
                    
                    // Cookie configuration for consistent user tracking
                    ...(config.options?.cookieFlags && { cookieFlags: config.options.cookieFlags }),
                    ...(config.options?.cookieDomain && { cookieDomain: config.options.cookieDomain }),
                    ...(config.options?.cookieExpires && { cookieExpires: config.options.cookieExpires }),
                    ...(config.options?.anonymizeIp && { anonymize_ip: config.options.anonymizeIp }),
                    
                    // Ensure cookies are used for session persistence
                    cookie_update: true,
                    cookie_prefix: '_ga',
                },
                gtagOptions: {
                    debug_mode: config.debug,
                    // Send page view automatically on initialization
                    send_page_view: false, // We handle this manually for better control
                },
            });

            this.initialized = true;

            if (config.debug) {
                console.log('Google Analytics initialized with measurement ID:', config.measurementId);
                console.log('User identification enabled with cookie persistence');
            }
        } catch (error) {
            console.error('Failed to initialize Google Analytics:', error);
            throw new Error('Analytics initialization failed');
        }
    }

    async trackPageView(pageView: PageView): Promise<void> {
        if (!this.isInitialized() || !this.config?.isActive()) {
            if (this.config?.debug) {
                console.log('Page view (not tracked):', pageView.toTrackingData());
            }
            return;
        }

        try {
            const trackingData = pageView.toTrackingData();

            ReactGA.send({
                hitType: 'pageview',
                page: trackingData.page_path,
                title: trackingData.page_title,
                ...(trackingData.page_location && { location: trackingData.page_location }),
            });

            if (this.config?.debug) {
                console.log('Page view tracked:', trackingData);
            }
        } catch (error) {
            console.error('Failed to track page view:', error);
        }
    }

    async trackEvent(event: AnalyticsEvent): Promise<void> {
        if (!this.isInitialized() || !this.config?.isActive()) {
            if (this.config?.debug) {
                console.log('Event (not tracked):', event.toTrackingData());
            }
            return;
        }

        try {
            const trackingData = event.toTrackingData();

            ReactGA.event({
                category: trackingData.category,
                action: trackingData.action,
                label: trackingData.label,
                value: trackingData.value,
                ...trackingData.metadata,
            });

            if (this.config?.debug) {
                console.log('Event tracked:', trackingData);
            }
        } catch (error) {
            console.error('Failed to track event:', error);
        }
    }

    async setUserProperties(properties: UserProperties): Promise<void> {
        if (!this.isInitialized() || !this.config?.isActive()) {
            if (this.config?.debug) {
                console.log('User properties (not set):', properties.toTrackingData());
            }
            return;
        }

        try {
            const trackingData = properties.toTrackingData();

            // Set user properties in ReactGA
            ReactGA.set(trackingData);
            
            // Also set user properties via gtag for consistency
            if (typeof window !== 'undefined' && (window as any).gtag) {
                (window as any).gtag('set', 'user_properties', trackingData);
            }

            if (this.config?.debug) {
                console.log('User properties set for consistent tracking:', trackingData);
            }
        } catch (error) {
            console.error('Failed to set user properties:', error);
        }
    }

    async setUserId(userId: string): Promise<void> {
        if (!this.isInitialized() || !this.config?.isActive()) {
            if (this.config?.debug) {
                console.log('User ID (not set):', userId);
            }
            return;
        }

        try {
            // Set user ID in both ReactGA and gtag for consistent tracking
            ReactGA.set({ userId });
            
            // Also set it via gtag to ensure it's properly configured
            if (typeof window !== 'undefined' && (window as any).gtag) {
                (window as any).gtag('config', this.config.measurementId, {
                    user_id: userId,
                });
            }

            if (this.config?.debug) {
                console.log('User ID set for consistent user tracking:', userId);
            }
        } catch (error) {
            console.error('Failed to set user ID:', error);
        }
    }

    async clearUserData(): Promise<void> {
        if (!this.isInitialized() || !this.config?.isActive()) {
            return;
        }

        try {
            // Clear user ID and custom dimensions
            ReactGA.set({
                userId: undefined,
                user_id: undefined,
                user_role: undefined,
                university_id: undefined,
                university_name: undefined,
            });

            if (this.config?.debug) {
                console.log('User data cleared');
            }
        } catch (error) {
            console.error('Failed to clear user data:', error);
        }
    }

    isInitialized(): boolean {
        return this.initialized;
    }
}
